import { useGoogleAdsConversions } from "@/Hooks/useGoogleAdsConversions";
import { useOnce } from "@/Hooks/useOnce";
import { useShipmentService } from "@/Services/ShipmentService";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import { useSavedBrokersApi } from "@/Hooks/useSavedBrokersApi";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { BookingFieldViolation } from "@freightsimple/generated-dashboard-openapi-client/models/BookingFieldViolation";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedBroker } from "@freightsimple/generated-dashboard-openapi-client/models/SavedBroker";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import {
  updateBroker,
  updateStripePaymentMethod,
} from "@/Slices/bookShipmentSlice";
import { bookShipmentViolationsFromError } from "./Helpers/bookShipmentViolationsFromError";
import { BookShipmentReviewPanel } from "./Internal/bookShipmentReviewPanel";

interface BookShipmentReviewScreenProps {
  shipment: PreBookingShipment;
  savedLocations: Array<SavedLocation>;
  onModifyDetails: () => void;
  onModifyPickupAddress: () => void;
  onModifyDeliveryAddress: () => void;
  onSelectAnotherQuote: () => void;
}

function BookShipmentReviewScreen(props: BookShipmentReviewScreenProps) {
  const { shipment, savedLocations } = props;
  const shipmentsService = useShipmentService();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { onFirstBookingWithValue } = useGoogleAdsConversions();

  const { shipmentId } = shipment;

  const [savedBrokers, setSavedBrokers] = useState<Array<SavedBroker>>([]);
  const paymentMethodsApi = usePaymentMethodsApi();
  const [cards, setCards] = useState<Array<CreditCard>>([]);
  const [bankAccounts, setBankAccounts] = useState<Array<BankAccount>>([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(true);
  const shipmentsApi = useShipmentsApi();
  const savedBrokersApi = useSavedBrokersApi();
  const [loadingSavedBrokers, setLoadingSavedBrokers] = useState(false);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [violations, setViolations] = useState<
    BookingFieldViolation[] | undefined
  >();

  async function loadSavedBrokers() {
    setLoadingSavedBrokers(true);
    await refreshSavedBrokers();
    setLoadingSavedBrokers(false);
  }

  // Background variant for refreshes triggered from inside the open
  // change-broker dialog. It must NOT flip `loadingSavedBrokers`: that swaps
  // the whole screen for <PageLoadingSpinner/>, which unmounts the dialog host
  // mid-flow — the dialog would vanish before the user clicks Done.
  async function refreshSavedBrokers() {
    const savedBrokersResponse = await savedBrokersApi.getAllSavedBrokers();
    setSavedBrokers(savedBrokersResponse.items);
  }

  async function loadCreditCards() {
    setLoadingCards(true);
    const creditCardsResponse = await paymentMethodsApi.getAllPaymentMethods();

    if (creditCardsResponse.creditCards === undefined) {
      throw new Error("no credit cards");
    }

    setCards(creditCardsResponse.creditCards);
    setLoadingCards(false);
  }

  async function loadBankAccounts() {
    setLoadingBankAccounts(true);
    try {
      const response = await paymentMethodsApi.getAllPaymentMethods();

      setBankAccounts(response.bankAccounts);
    } catch (e) {
      console.error("Error with loadBankAccounts", { e });
      setBankAccounts([]);
    }

    setLoadingBankAccounts(false);
  }

  useOnce(async function () {
    shipmentsService.markReviewingPriorToBookingShipment(shipment.shipmentId);
  });

  useOnce(async function () {
    await loadSavedBrokers();
  });

  useOnce(async function () {
    await loadCreditCards();
  });

  useOnce(async function () {
    await loadBankAccounts();
  });

  async function onBookShipment() {
    if (bookingInProgress) {
      return; // Prevent double booking
    }

    setBookingInProgress(true);
    setViolations(undefined);

    // Clear the requote with lift gate flag from localStorage
    localStorage.removeItem("requotedWithLiftGate");

    try {
      const response = await shipmentsService.bookShipment(shipmentId);

      // Fire Google Ads conversion for first booking if backend determined this is a valuable shipper
      if (
        response.shouldFireFirstBookingConversion &&
        response.firstBookingConversionValue !== undefined
      ) {
        onFirstBookingWithValue(response.firstBookingConversionValue);
      }

      navigate(`/book/processing?shipmentId=${shipmentId}`);
    } catch (e) {
      // A refusal the customer can fix (a contact, a deadline) stays on this
      // screen with the reasons; anything else is ours to finish by hand.
      const refused = await bookShipmentViolationsFromError(e);
      if (refused !== undefined) {
        setViolations(refused);
        setBookingInProgress(false);
        return;
      }
      console.error(e);
      navigate(`/book/nearlydone?shipmentId=${shipmentId}`);
    }
    // bookingInProgress stays set on the two navigating paths: the screen is going away.
  }

  if (loadingSavedBrokers) {
    return <PageLoadingSpinner />;
  }

  function findSavedBrokerId() {
    const broker = shipment.broker;

    const matchingSavedBroker = savedBrokers.find(function (b) {
      return (
        b.broker.businessName === broker?.businessName &&
        b.broker.address.postalCode === broker.address.postalCode
      );
    });

    return matchingSavedBroker?.savedBrokerId;
  }

  const savedBrokerId = findSavedBrokerId();

  const selectedStripePaymentMethodId = shipment.stripePaymentMethodId;

  async function onCreditCardChanged(newStripePaymentMethod: string) {
    dispatch(updateStripePaymentMethod(newStripePaymentMethod));
    await shipmentsApi.setStripePaymentMethodId({
      getStartedSetStripePaymentMethodId: {
        stripePaymentMethodId: newStripePaymentMethod,
        shipmentId: shipmentId,
        newPaymentMethod: true,
      },
    });
  }

  async function onBrokerChanged(updatedBroker: Broker) {
    await shipmentsApi.setBrokerDetails({
      setBrokerDetailsRequest: {
        shipmentId: props.shipment.shipmentId,
        broker: updatedBroker,
      },
    });
    await refreshSavedBrokers();
    dispatch(updateBroker(updatedBroker));
  }

  if (
    shipment === undefined ||
    loadingCards === true ||
    loadingBankAccounts === true
  ) {
    return <PageLoadingSpinner />;
  }

  return (
    <BookShipmentReviewPanel
      shipment={shipment}
      savedLocations={savedLocations}
      onBookShipment={onBookShipment}
      savedBrokerId={savedBrokerId}
      savedBrokers={savedBrokers}
      cards={cards}
      bankAccounts={bankAccounts}
      selectedStripePaymentMethodId={selectedStripePaymentMethodId}
      onCreditCardChanged={onCreditCardChanged}
      onModifyDetails={props.onModifyDetails}
      onModifyPickupAddress={props.onModifyPickupAddress}
      onModifyDeliveryAddress={props.onModifyDeliveryAddress}
      onSelectAnotherQuote={props.onSelectAnotherQuote}
      onBrokerChanged={onBrokerChanged}
      bookingInProgress={bookingInProgress}
      violations={violations}
    />
  );
}

export default BookShipmentReviewScreen;
